import { NavLink } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import {
  ChartBar,
  ChatCircle,
  Robot,
  Gear,
  CalendarCheck,
  Tag,
  Translate,
  SignOut
} from '@phosphor-icons/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

function LogoutButton() {
  const { logout, user } = useAuth();
  return (
    <button
      onClick={logout}
      className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 transition-colors text-sm"
    >
      <SignOut size={16} />
      <span className="flex-1 text-left truncate">{user?.name || 'Log ud'}</span>
      <span className="text-xs text-zinc-600">Log ud</span>
    </button>
  );
}

export function Sidebar() {
  const { t, language, setLanguage } = useLanguage();
  
  const navItems = [
    { path: '/', icon: ChartBar, label: t('dashboard') },
    { path: '/conversations', icon: ChatCircle, label: t('conversations') },
    { path: '/bot-config', icon: Robot, label: t('botConfig') },
    { path: '/bookings', icon: CalendarCheck, label: t('bookings') },
    { path: '/services', icon: Tag, label: t('services') },
  ];
  
  return (
    <aside className="bg-zinc-950 text-zinc-400 w-64 min-h-screen flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Robot size={24} weight="duotone" className="text-white" />
          </div>
          <div>
            <h1 className="text-white font-heading font-bold text-lg leading-tight">
              {t('schoolAIBot')}
            </h1>
            <p className="text-xs text-zinc-500">META Integration</p>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-4">
        {navItems.map((item) => (
          // NavLink keeps navigation inside the SPA: only <main> re-renders, the
          // sidebar stays mounted. A plain <a href> reloaded the whole document on
          // every click. `end` limits the root item to an exact match, so "/" is not
          // highlighted on every page; the others match nested paths as before.
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm transition-colors duration-200 ${
                isActive
                  ? 'bg-zinc-800 text-white border-l-2 border-l-blue-600'
                  : 'hover:bg-zinc-900 hover:text-zinc-200'
              }`
            }
          >
            <item.icon size={20} weight="duotone" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      
      {/* Language Selector */}
      <div className="p-4 border-t border-zinc-800">
        <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500">
          <Translate size={14} />
          {t('language')}
        </div>
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger 
            className="w-full bg-zinc-900 border-zinc-800 text-zinc-300"
            data-testid="language-selector"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="da" data-testid="lang-da">Dansk</SelectItem>
            <SelectItem value="en" data-testid="lang-en">English</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Footer */}
      <div className="p-4 border-t border-zinc-800 space-y-2">
        <LogoutButton />
        <div className="text-xs text-zinc-600">
          Powered by Claude Opus 4.6
        </div>
      </div>
    </aside>
  );
}
